import { useCallback } from 'react';
import { usePlan } from '../../../context/PlanContext';

/**
 * Plan features that hide controls in the category and page views
 * (contracts/PLAN-CP.md §1–§2). The API refuses the action anyway (403
 * `wiki_feature_disabled`); hiding just stops offering what the plan turns off.
 *
 *   wiki_import               category "Import"
 *   wiki_category_assistants  "Ask {Category}", "Ask about this page"
 *                             (a category-scoped Ask is refused without it)
 *   wiki_acks                 "Remind the rest" (POST …/ack stays open)
 */
export const WIKI_PLAN_FEATURES = Object.freeze({
    import: 'wiki_import',
    categoryAssistants: 'wiki_category_assistants',
    acks: 'wiki_acks',
});

/**
 * Pure rule, exported for tests: while the plan is unknown (loading, or no
 * PlanProvider) the control shows and the server decides; once known, it
 * shows only when the feature is on.
 */
export function planAllows(plan, key) {
    if (!plan || plan.planUnknown) return true;
    return typeof plan.checkFeature === 'function' ? plan.checkFeature(key) === true : true;
}

/**
 * The per-category "Category assistant" switch (`assistant_enabled`, prototype
 * "Adds an 'Ask' button that answers only from this category"). A category-
 * scoped Ask is refused (403 `wiki_category_assistant_off`) when the category
 * or its parent has it off; `undefined` (field not sent) counts as on.
 */
export function categoryAssistantOn(...categories) {
    return categories.every((category) => !category || category.assistant_enabled !== false);
}

/** `const allows = useWikiPlanAllows(); allows(WIKI_PLAN_FEATURES.import)`. */
export default function useWikiPlanAllows() {
    const plan = usePlan();
    const { planUnknown, checkFeature } = plan;
    return useCallback(
        (key) => planAllows({ planUnknown, checkFeature }, key),
        [planUnknown, checkFeature],
    );
}
